/**
 * Admin Menu Builder — visual menu management
 *
 * Shows the live menu of Hardware Categories + Solutions exactly as it
 * appears on the public site. Admin can:
 *  - Drag rows to reorder (persisted to `order` field in DB)
 *  - Click "Editar" to open the existing CrudForm (renames, slug, icon, desc)
 *  - See "Vacía" badge on hw categories with 0 products (hidden from public)
 *
 * Uses /api/hardware/categories + /api/solutions for the live snapshot,
 * /api/admin/hw-categories-reorder + /api/admin/solutions-reorder for persistence.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, GripVertical, Pencil, Eye, EyeOff, Save, RefreshCw, Package, Layers } from 'lucide-react';
import { API, adminApi, useAuth } from '@/components/admin/AdminShell';
import { CrudForm } from '@/components/admin/CrudForm';
import { hwCategoryFields, solutionFields } from '@/components/admin/fields';
import { resolveIcon } from '@/lib/iconMap';

const SectionCard = ({ title, subtitle, items, onReorder, onEdit, onRefresh, type, badge, hiddenSlugs }) => {
  const [dragIdx, setDragIdx] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = items.slice();
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(idx);
    onReorder(next);
    setDirty(true);
  };
  const onDragEnd = () => setDragIdx(null);

  const save = async () => {
    setSaving(true);
    try {
      await onReorder(items, true); // commits to backend
      setDirty(false);
      toast.success(`Orden de ${title} guardado`);
    } catch (e) {
      toast.error(`Error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`menu-builder-section-${type}`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {badge}
          <div>
            <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Syne,sans-serif' }}>{title}</h2>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="p-2 text-slate-400 hover:text-[#0EA5E9] rounded-lg" title="Recargar desde DB" data-testid={`menu-refresh-${type}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`menu-save-order-${type}`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Guardar orden
          </button>
        </div>
      </div>

      <ul className="divide-y divide-slate-100">
        {items.length === 0 && (
          <li className="px-5 py-8 text-center text-sm text-slate-400">No hay elementos. Créalos desde la página correspondiente.</li>
        )}
        {items.map((item, idx) => {
          const Icon = resolveIcon(item.icon, type === 'hardware' ? Package : Layers);
          const slug = item.slug;
          const isHidden = hiddenSlugs?.has(slug);
          return (
            <li
              key={item.id || slug}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={(e) => onDragOver(e, idx)}
              onDragEnd={onDragEnd}
              className={`group flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors cursor-move ${dragIdx === idx ? 'opacity-50 bg-sky-50' : ''}`}
              data-testid={`menu-item-${type}-${slug}`}
            >
              <GripVertical className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
              <span className="tabular-nums text-[11px] text-slate-400 font-mono w-6">{idx + 1}.</span>
              <Icon className="w-5 h-5 text-[#0EA5E9] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900 truncate">{item.name || item.title}</span>
                  {isHidden && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full font-semibold uppercase tracking-wider" title="Oculta del menú público porque no tiene productos">
                      <EyeOff className="w-3 h-3" /> Vacía · oculta
                    </span>
                  )}
                  {!isHidden && type === 'hardware' && item.product_count !== undefined && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-semibold tabular-nums">
                      <Eye className="w-3 h-3" /> {item.product_count} prod.
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">
                  {type === 'hardware' ? `/hardware/${slug}` : `/soluciones/${slug}`}
                </div>
              </div>
              <button
                onClick={() => onEdit(item)}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0EA5E9] bg-sky-50 rounded-lg hover:bg-sky-100"
                data-testid={`menu-edit-${type}-${slug}`}
              >
                <Pencil className="w-3.5 h-3.5" /> Editar
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default function MenuBuilder() {
  const auth = useAuth();
  const [hwCats, setHwCats] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [activeCategorySlugs, setActiveCategorySlugs] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { type:'hardware'|'solutions', item }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, sols, active] = await Promise.all([
        axios.get(`${API}/hardware/categories`).then(r => r.data),
        axios.get(`${API}/solutions`).then(r => r.data),
        axios.get(`${API}/products/categories`).then(r => r.data).catch(() => []),
      ]);
      const sortedCats = (cats || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      const sortedSols = (sols || []).slice().sort((a, b) => (a.order ?? a.display_order ?? 999) - (b.order ?? b.display_order ?? 999));
      setHwCats(sortedCats);
      setSolutions(sortedSols);
      setActiveCategorySlugs(new Set((active || []).map(c => c.slug)));
    } catch (e) {
      toast.error('Error al cargar el menú');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reorderHw = async (next, commit = false) => {
    setHwCats(next);
    if (commit) {
      const payload = { items: next.map((c, i) => ({ id: c.id, order: i + 1 })) };
      await adminApi(auth.token).put('/admin/hw-categories-reorder', payload);
      // also regen sitemap so order changes propagate to lastmod
      try { await adminApi(auth.token).post('/admin/sitemap/regenerate'); } catch (_) {}
    }
  };

  const reorderSol = async (next, commit = false) => {
    setSolutions(next);
    if (commit) {
      const payload = { items: next.map((s, i) => ({ id: s.id, order: i + 1 })) };
      await adminApi(auth.token).put('/admin/solutions-reorder', payload);
      try { await adminApi(auth.token).post('/admin/sitemap/regenerate'); } catch (_) {}
    }
  };

  const saveItem = async (data) => {
    if (!editing) return;
    const { type } = editing;
    const collection = type === 'hardware' ? 'hw-categories' : 'solutions';
    try {
      if (data.id) {
        await adminApi(auth.token).put(`/admin/${collection}/${data.id}`, data);
        toast.success('Actualizado');
      } else {
        await adminApi(auth.token).post(`/admin/${collection}`, data);
        toast.success('Creado');
      }
      setEditing(null);
      load();
      try { await adminApi(auth.token).post('/admin/sitemap/regenerate'); } catch (_) {}
    } catch (e) {
      toast.error(`Error: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (editing) {
    return (
      <CrudForm
        item={editing.item}
        fields={editing.type === 'hardware' ? hwCategoryFields : solutionFields}
        onSave={saveItem}
        onCancel={() => setEditing(null)}
        title={editing.type === 'hardware' ? 'Categoría Hardware' : 'Solución'}
      />
    );
  }

  return (
    <div data-testid="admin-menu-builder" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Syne,sans-serif' }}>Menú Principal</h1>
        <p className="text-sm text-slate-500 mt-1">
          Vista en vivo del menú público (Hardware + Soluciones). Arrastra para reordenar, click "Editar" para renombrar.
          Los cambios se reflejan en el sitio inmediatamente al guardar — sin redeploy.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <SectionCard
            title="Hardware"
            subtitle={`${hwCats.length} categorías · ${activeCategorySlugs.size} activas en menú público`}
            items={hwCats}
            type="hardware"
            badge={<div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Package className="w-5 h-5 text-[#0EA5E9]" /></div>}
            hiddenSlugs={new Set(hwCats.map(c => c.slug).filter(s => !activeCategorySlugs.has(s)))}
            onReorder={reorderHw}
            onEdit={(item) => setEditing({ type: 'hardware', item })}
            onRefresh={load}
          />
          <SectionCard
            title="Soluciones"
            subtitle={`${solutions.length} soluciones en el menú público`}
            items={solutions}
            type="solutions"
            badge={<div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Layers className="w-5 h-5 text-amber-600" /></div>}
            onReorder={reorderSol}
            onEdit={(item) => setEditing({ type: 'solutions', item })}
            onRefresh={load}
          />

          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-xs text-sky-900 leading-relaxed">
            <p className="font-semibold mb-1">💡 ¿Cómo funciona?</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>El menú del sitio se construye en vivo desde estos datos en la DB.</li>
              <li>Las categorías sin productos se ocultan automáticamente del menú público (badge <strong>Vacía · oculta</strong>) — pero siguen accesibles por URL directa.</li>
              <li>Al guardar reordenamiento, el <code className="bg-white px-1 rounded">sitemap.xml</code> se regenera automáticamente.</li>
              <li>Para Software, IA, Servicios y Empresa, los ítems siguen siendo páginas estáticas (no son CRUD).</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
